import { PushpinFilled } from '@ant-design/icons';
import { Tag } from 'antd';
import { operatorRequiresValue } from '../core/operators';
import type { Filter, FilterConfig } from '../core/types';
import { FilterOperator } from './FilterOperator';
import { FilterValue } from './FilterValue';

interface FilterFieldProps<TWhere> {
    filter: Filter;
    config: FilterConfig<TWhere>;
    whereClause?: TWhere;
    shouldFocus?: boolean;
    onUpdate: (updates: Partial<Filter>) => void;
    onRemove?: () => void;
    onFocused?: () => void;
}

export function FilterField<TWhere>({
    filter,
    config,
    whereClause,
    shouldFocus,
    onUpdate,
    onRemove,
    onFocused,
}: FilterFieldProps<TWhere>) {
    const fieldConfig = config.filters.find((f) => f.id === filter.filterId);
    if (!fieldConfig) return null;

    const showValueInput = operatorRequiresValue(filter.operator);
    const isPinned = fieldConfig.pinned === true;
    const isTextSearch = fieldConfig.type === 'textSearch';

    // For textSearch fields, show simplified UI without operator selector
    if (isTextSearch) {
        return (
            <>
                {/* Value input - always shown for textSearch */}
                <div className="inline-block min-w-[90px] max-w-[400px]">
                    <FilterValue
                        value={filter.value}
                        operator={filter.operator}
                        fieldConfig={fieldConfig}
                        whereClause={whereClause}
                        autoFocus={shouldFocus}
                        onChange={(value) => onUpdate({ value })}
                        onFocused={onFocused}
                    />
                </div>
            </>
        );
    }

    // Default rendering for other field types
    return (
        <Tag
            closable={!isPinned && !!onRemove}
            onClose={onRemove}
            className={`group me-0 mb-0 inline-flex w-auto ${showValueInput ? 'min-w-[180px]' : 'min-w-0'} max-w-none flex-wrap items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-all ${
                isPinned
                    ? 'border-accent-200/80 bg-card hover:border-accent-300'
                    : 'border-separator bg-card hover:border-subtle-foreground'
            }`}
        >
            {/* Pin icon for pinned fields */}
            {isPinned && <PushpinFilled className="text-accent-500" style={{ fontSize: '12px' }} />}

            {/* Field label */}
            <span className="truncate font-medium text-muted-foreground">{fieldConfig.label}</span>

            {/* Operator selector */}
            <FilterOperator
                value={filter.operator}
                fieldType={fieldConfig.type || 'text'}
                customOperators={fieldConfig.operators}
                onChange={(operator) => onUpdate({ operator })}
            />

            {/* Value input */}
            {showValueInput && (
                <div className="inline-block min-w-[90px] max-w-[400px]">
                    <FilterValue
                        value={filter.value}
                        operator={filter.operator}
                        fieldConfig={fieldConfig}
                        whereClause={whereClause}
                        autoFocus={shouldFocus}
                        onChange={(value) => onUpdate({ value })}
                        onFocused={onFocused}
                    />
                </div>
            )}
        </Tag>
    );
}
